import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from "@mui/material";
import axios from "axios";

export default function AddSingleUnitDialog({
  open,
  onClose,
  onAdd,
  structures = [],
}) {
  const [type, setType] = useState("Tower");

  const [structureName, setStructureName] = useState("");
  const [isNewStructure, setIsNewStructure] = useState(false);
  const [newStructureName, setNewStructureName] = useState("");

  const [floor, setFloor] = useState(1);
  const [unitNumber, setUnitNumber] = useState(1);
  const [customName, setCustomName] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const structureLabel = type === "Tower" ? "Tower" : "Villa";
  const unitLabel = type === "Tower" ? "Flat" : "Villa";

  const filteredStructures = structures.filter(
    (s) => s.type.toLowerCase() === type.toLowerCase()
  );

  useEffect(() => {
    if (open) {
      setType("Tower");
      setStructureName("");
      setIsNewStructure(false);
      setNewStructureName("");
      setFloor(1);
      setUnitNumber(1);
      setCustomName("");
    }
  }, [open]);

  useEffect(() => {
    setStructureName("");
    setIsNewStructure(false);
    setNewStructureName("");
  }, [type]);

  const selectedStructureName = isNewStructure
    ? newStructureName.trim()
    : structureName;

  const generatedName = `${unitLabel} ${String(floor).padStart(2, "0")}${String(
    unitNumber
  ).padStart(2, "0")} ${structureLabel} ${selectedStructureName}`.trim();

  const handleStructureChange = (event) => {
    const val = event.target.value;
    if (val === "new") {
      setIsNewStructure(true);
      setStructureName("");
    } else {
      setIsNewStructure(false);
      setStructureName(val);
    }
  };

  const handleAdd = async () => {
    if (isNewStructure && !newStructureName.trim()) {
      alert(`Please enter a new ${structureLabel} name.`);
      return;
    }

    if (!isNewStructure && !structureName) {
      alert(`Please select an existing ${structureLabel} or add a new one.`);
      return;
    }

    const payload = {
      buildingType: type,
      buildingName: selectedStructureName,
      floor: floor.toString(),
      flat_number: unitNumber.toString(),
      unitName: customName.trim() || generatedName,
    };

    try {
      setSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/units/create`,
        payload,
        { withCredentials: true }
      );

      // Success toast can be shown here
      onAdd(response.data); // optionally pass response
      onClose();
    } catch (error) {
      console.error("Unit creation failed:", error);
      alert(
        error?.response?.data?.error || "Something went wrong while creating unit."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Single Unit</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <FormControlLabel value="Tower" control={<Radio />} label="Tower" />
              <FormControlLabel value="Villa" control={<Radio />} label="Villa" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box mb={2}>
          <FormControl fullWidth>
            <InputLabel id="structure-select-label">
              {structureLabel} Name
            </InputLabel>
            <Select
              labelId="structure-select-label"
              value={isNewStructure ? "new" : structureName}
              onChange={handleStructureChange}
              fullWidth
            >
              {filteredStructures.map((s) => (
                <MenuItem key={s.id} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
              <MenuItem value="new">➕ Add new {structureLabel}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isNewStructure && (
          <Box mb={2}>
            <TextField
              fullWidth
              label={`New ${structureLabel} Name`}
              value={newStructureName}
              onChange={(e) => setNewStructureName(e.target.value)}
            />
          </Box>
        )}

        <Box display="flex" gap={2} mb={2}>
          <TextField
            label={type === "Tower" ? "Floor" : "Block"}
            type="number"
            inputProps={{ min: 1 }}
            value={floor}
            onChange={(e) => setFloor(Number(e.target.value) || 1)}
            fullWidth
          />
          <TextField
            label={type === "Tower" ? "Flat Number" : "Villa Number"}
            type="number"
            inputProps={{ min: 1 }}
            value={unitNumber}
            onChange={(e) => setUnitNumber(Number(e.target.value) || 1)}
            fullWidth
          />
        </Box>

        <Box mb={2}>
          <TextField
            fullWidth
            label="Unit Name (optional)"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={`e.g. ${generatedName}`}
            helperText="Leave empty to auto-generate"
          />
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle1" color="textSecondary">
            Generated Unit Name:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {customName.trim() || generatedName || "—"}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={submitting}
        >
          {submitting ? "Adding..." : "Add Unit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
